import { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { resolveArtworkUrl, PRIORITY_COLORS, LOCATION_COLORS, api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, ExternalLink, ShoppingBag, Tag, Calendar, Disc3, Hash, Loader2, Mail } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const CONDITIONS = ["Poor", "Good", "Excellent", "Near Mint", "Mint"];
const LOCATIONS = ["Ruth", "Andy", "Archive", "Lagom"];
const SPEEDS = ["33", "45", "78"];

function Field({ label, value, icon: Icon }) {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div className="flex gap-3">
            {Icon && <Icon className="w-4 h-4 text-muted-foreground mt-1 shrink-0" strokeWidth={1.5} />}
            <div>
                <p className="overline text-muted-foreground">{label}</p>
                <p className="text-sm mt-0.5">{value}</p>
            </div>
        </div>
    );
}

export default function WishlistDetailDialog({ item, onOpenChange, onEdit, onDelete, onPurchase }) {
    const [buyOpen, setBuyOpen] = useState(false);
    const [purchase, setPurchase] = useState({
        location: "Andy", purchase_price: "", purchase_date: "",
        speed: "33", media_condition: "Good", sleeve_condition: "Good",
    });
    const [submitting, setSubmitting] = useState(false);

    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState(() => {
        try { return localStorage.getItem("last_supplier_email") || ""; } catch { return ""; }
    });
    const [emailMessage, setEmailMessage] = useState("");
    const [sending, setSending] = useState(false);

    const sendEmail = async () => {
        if (!emailTo.trim()) {
            toast.error("Please enter an email address");
            return;
        }
        setSending(true);
        try {
            await api.post(`/wishlist/${item.id}/email`, {
                to: emailTo.trim(),
                message: emailMessage.trim() || null,
            });
            try { localStorage.setItem("last_supplier_email", emailTo.trim()); } catch { /* ignore */ }
            toast.success(`Email sent to ${emailTo}`);
            setEmailOpen(false);
            setEmailMessage("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send email");
        } finally {
            setSending(false);
        }
    };

    if (!item) {
        return (
            <Dialog open={false} onOpenChange={onOpenChange}>
                <DialogContent />
            </Dialog>
        );
    }

    const setP = (k, v) => setPurchase(p => ({ ...p, [k]: v }));
    const submitPurchase = async () => {
        setSubmitting(true);
        const payload = {
            location: purchase.location,
            speed: purchase.speed,
            media_condition: purchase.media_condition,
            sleeve_condition: purchase.sleeve_condition,
            purchase_price: purchase.purchase_price !== "" ? parseFloat(purchase.purchase_price) : null,
            purchase_date: purchase.purchase_date || null,
        };
        await onPurchase(item.id, payload);
        setSubmitting(false);
        setBuyOpen(false);
    };

    return (
        <Dialog open={!!item} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="wishlist-detail-dialog">
                <DialogHeader>
                    <DialogDescription className="sr-only">Wishlist details for {item.album_title}</DialogDescription>
                </DialogHeader>
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="aspect-square rounded-md overflow-hidden bg-secondary border border-border">
                        <img src={resolveArtworkUrl(item)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="space-y-4">
                        <div>
                            <p className="overline text-muted-foreground">{item.artist}</p>
                            <DialogTitle asChild>
                                <h2 className="font-display text-4xl font-bold leading-none tracking-tight mt-2">
                                    {item.album_title}
                                </h2>
                            </DialogTitle>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className={`px-3 py-1 text-xs rounded-full border ${PRIORITY_COLORS[item.priority] || ""}`}>
                                {item.priority}
                            </span>
                            {item.target_price != null && (
                                <span className="px-3 py-1 text-xs rounded-full border border-border bg-muted text-muted-foreground font-mono">
                                    Target £{item.target_price.toFixed(2)}
                                </span>
                            )}
                        </div>

                        {item.discogs_id && (
                            <div className="flex items-center gap-2 pt-1">
                                <span className="overline text-muted-foreground">Discogs</span>
                                <a
                                    href={item.discogs_url || `https://www.discogs.com/release/${item.discogs_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-mono"
                                    data-testid="wishlist-discogs-external-link"
                                >
                                    #{item.discogs_id}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <Field label="Release year" value={item.release_year} icon={Calendar} />
                            <Field label="Genre" value={item.genre} icon={Tag} />
                            <Field label="Label" value={item.label} icon={Disc3} />
                            <Field label="Catalog #" value={item.catalog_number} icon={Hash} />
                        </div>

                        {item.notes && (
                            <div className="pt-2">
                                <p className="overline text-muted-foreground mb-1">Notes</p>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{item.notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {item.vendors && item.vendors.length > 0 && (
                    <div className="pt-4 border-t border-border" data-testid="wishlist-vendors-list">
                        <p className="overline text-muted-foreground mb-3">Where to buy</p>
                        <div className="space-y-2">
                            {item.vendors.map((v, i) => (
                                <a
                                    key={i}
                                    href={v.url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => !v.url && e.preventDefault()}
                                    className={`flex items-center gap-3 p-3 rounded-md border border-border ${v.url ? "hover:border-primary hover:bg-primary/5" : "opacity-60 cursor-default"} transition-colors`}
                                    data-testid={`wishlist-vendor-item-${i}`}
                                >
                                    <span className="text-sm font-medium min-w-[140px]">{v.vendor}</span>
                                    <span className="text-xs text-muted-foreground flex-1 truncate">
                                        {v.url || "No URL"}
                                    </span>
                                    {v.price != null && (
                                        <span className="text-sm font-mono text-primary shrink-0">
                                            £{v.price.toFixed(2)}
                                        </span>
                                    )}
                                    {v.url && <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                    <Button
                        onClick={() => setBuyOpen(true)}
                        className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="wishlist-bought-btn"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        Bought it
                    </Button>
                    <Button
                        onClick={() => setEmailOpen(true)}
                        variant="outline"
                        className="gap-2"
                        data-testid="wishlist-email-btn"
                    >
                        <Mail className="w-4 h-4" />
                        Email supplier
                    </Button>
                    <Button
                        onClick={() => onEdit(item)}
                        variant="outline"
                        className="gap-2"
                        data-testid="wishlist-edit-btn"
                    >
                        <Pencil className="w-4 h-4" />
                        Edit
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive ml-auto"
                                data-testid="wishlist-delete-btn"
                            >
                                <Trash2 className="w-4 h-4" />
                                Remove
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Remove from wishlist?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    &ldquo;{item.album_title}&rdquo; will be removed from your wishlist.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="wishlist-cancel-delete-btn">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() => onDelete(item.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    data-testid="wishlist-confirm-delete-btn"
                                >
                                    Remove
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </DialogContent>

            {/* "Bought it" flow */}
            <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
                <DialogContent className="max-w-lg bg-card" data-testid="purchase-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                            Add to collection
                        </DialogTitle>
                        <DialogDescription>
                            &ldquo;{item.album_title}&rdquo; will move from your wishlist into the collection.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-2">
                        <div>
                            <Label className="overline">Location</Label>
                            <Select value={purchase.location} onValueChange={(v) => setP("location", v)}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-location"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LOCATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline">Speed</Label>
                            <Select value={purchase.speed} onValueChange={(v) => setP("speed", v)}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-speed"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {SPEEDS.map(s => <SelectItem key={s} value={s}>{s} rpm</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline">Media condition</Label>
                            <Select value={purchase.media_condition} onValueChange={(v) => setP("media_condition", v)}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-media"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline">Sleeve condition</Label>
                            <Select value={purchase.sleeve_condition} onValueChange={(v) => setP("sleeve_condition", v)}>
                                <SelectTrigger className="mt-1.5" data-testid="purchase-sleeve"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline">Purchase price</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={purchase.purchase_price}
                                onChange={(e) => setP("purchase_price", e.target.value)}
                                className="mt-1.5"
                                data-testid="purchase-price"
                            />
                        </div>
                        <div>
                            <Label className="overline">Purchase date</Label>
                            <div className="mt-1.5">
                                <DatePicker
                                    value={purchase.purchase_date}
                                    onChange={(v) => setP("purchase_date", v)}
                                    placeholder="Pick a date"
                                    testId="purchase-date"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBuyOpen(false)}>Cancel</Button>
                        <Button
                            onClick={submitPurchase}
                            disabled={submitting}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                            data-testid="purchase-confirm-btn"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Move to collection
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* "Email supplier" flow */}
            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent className="max-w-lg bg-card" data-testid="email-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                            Email a supplier
                        </DialogTitle>
                        <DialogDescription>
                            We&apos;ll send them the artist, album, Discogs link, target price and any notes.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="overline">Recipient email</Label>
                            <Input
                                type="email"
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                placeholder="supplier@example.com"
                                className="mt-1.5"
                                autoFocus
                                data-testid="email-to-input"
                            />
                        </div>
                        <div>
                            <Label className="overline">Personal message (optional)</Label>
                            <Textarea
                                value={emailMessage}
                                onChange={(e) => setEmailMessage(e.target.value)}
                                placeholder="Hey — do you have this in stock? Any condition notes appreciated."
                                rows={4}
                                className="mt-1.5"
                                data-testid="email-message-input"
                            />
                        </div>
                        <div className="rounded-md bg-secondary/60 border border-border p-3 text-xs text-muted-foreground">
                            <p className="font-medium text-foreground mb-1">What they&apos;ll see</p>
                            <p>Album cover, {item.artist} — {item.album_title}, Discogs link{item.target_price != null ? `, target £${item.target_price.toFixed(2)}` : ""}{item.notes ? ", your notes" : ""}.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
                        <Button
                            onClick={sendEmail}
                            disabled={sending}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                            data-testid="email-send-btn"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            Send email
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
